import { Menu, MenuItem, MenuList, useDisclosure } from "@chakra-ui/react";
import { ModalBrand } from "@components/modules/brands/ModalBrand";
import { Trans, t } from "@lingui/macro";
import {
  type BrandModalFragment,
  type Client,
  useArchiveBrandMutation,
  useDeleteBrandMutation,
  useUnarchiveBrandMutation,
} from "@src/__generated__/graphql";
import { MenuItemLink, ModalConfirm } from "@src/components/ui-kit";
import { onError } from "@src/utils/apollo";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { assert } from "@src/utils/types";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import React, { type FunctionComponent, type ReactNode } from "react";
import type { Store } from "./Store";

type Props = {
  trigger: ReactNode;
  brand: Omit<Store["brandForModal"], "defaultBillingEntity">;
  client: Pick<Client, "id" | "name">;
  onAfterUpdate?: (brand: BrandModalFragment) => void;
  onAfterDelete?: () => void;
  hideClientLink?: boolean;
};

export const PopoverBrandActions: FunctionComponent<
  React.PropsWithChildren<Props>
> = observer(function PopoverBrandActions({
  trigger,
  brand,
  onAfterDelete,
  onAfterUpdate,
  client,
  hideClientLink = true,
}) {
  const appStore = useStore();
  assert(brand);

  const r = useRouter();

  const editModal = useDisclosure();
  const deleteModal = useDisclosure();
  const archiveModal = useDisclosure();
  const unarchiveModal = useDisclosure();

  const [del] = useDeleteBrandMutation({
    onCompleted() {
      appStore.UIStore.toast({
        title: t`The brand has been deleted`,
        status: "success",
      });
      onAfterDelete?.();
    },
    ...onError(),
  });

  const [archiveBrand] = useArchiveBrandMutation({
    onCompleted() {
      archiveModal.onClose();
      appStore.UIStore.toast({
        title: t`The brand has been archived`,
        status: "success",
      });
      r.reload();
    },
    ...onError(),
  });

  const [unarchiveBrand] = useUnarchiveBrandMutation({
    onCompleted() {
      unarchiveModal.onClose();
      appStore.UIStore.toast({
        title: t`The brand has been restored`,
        status: "success",
      });
      r.reload();
    },
    ...onError(),
  });

  const onEditBrand = (payload: BrandModalFragment) => {
    editModal.onClose();
    onAfterUpdate?.(payload);
  };

  return (
    <React.Fragment>
      <Menu>
        {trigger}
        <MenuList>
          {can("client_read") && !hideClientLink && (
            <MenuItemLink
              href={{ pathname: "/clients/detail", query: { id: client.id } }}
            >
              <Trans>Go to client detail</Trans>&nbsp;({client.name})
            </MenuItemLink>
          )}
          {can("brand_update") && (
            <MenuItem onClick={editModal.onOpen}>{t`Edit`}</MenuItem>
          )}
          {can("brand_delete") && (
            <MenuItem {...deleteModal.getButtonProps()}>{t`Delete`}</MenuItem>
          )}
          {can("brand_archive") &&
            (brand.archived_at ? (
              <MenuItem
                {...unarchiveModal.getButtonProps()}
              >{t`Restore`}</MenuItem>
            ) : (
              <MenuItem
                {...archiveModal.getButtonProps()}
              >{t`Archive`}</MenuItem>
            ))}
        </MenuList>
      </Menu>

      <ModalBrand
        {...editModal}
        brand={brand}
        clientId={client.id}
        onSubmit={onEditBrand}
      />

      <ModalConfirm
        {...deleteModal.getDisclosureProps()}
        onConfirm={() => del({ variables: { id: brand.id } })}
        {...deleteModal}
      />

      <ModalConfirm
        {...archiveModal.getDisclosureProps()}
        onConfirm={() => archiveBrand({ variables: { id: brand.id } })}
        {...archiveModal}
      />

      <ModalConfirm
        {...unarchiveModal.getDisclosureProps()}
        onConfirm={() => unarchiveBrand({ variables: { id: brand.id } })}
        {...unarchiveModal}
      />
    </React.Fragment>
  );
});
